
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isNativePlatform } from '@/lib/platform';
import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { error } from '@/utils/logger';
import { WebAnalytics } from '@/lib/analytics';
import { EVENT_DEEP_LINK_OPENED } from '@/lib/analyticsEvents';
import { resolveNativeDeepLinkTarget, supportsNativeDeepLinkUrl } from '@/utils/nativeDeepLinkRouting';
import { useInspoImport } from '@/contexts/InspoImportContext';

/**
 * Listens for `appUrlOpen` events on native platforms and routes Cognito OAuth
 * deep links (e.g. `com.globethrivers.app://auth/callback?code=...&state=...`)
 * into the existing /auth/callback page so the standard token-exchange flow runs.
 *
 * Inspo import share URLs open the global modal via context (no `/inspo-import` route).
 *
 * No-op on web.
 */
export default function NativeDeepLinkListener() {
    const router = useNavigate();
    const { openInspoImport } = useInspoImport();

    useEffect(() => {
        if (!isNativePlatform()) return;

        const handleIncomingUrl = (incomingUrl: string, source: 'launch' | 'event') => {
            if (!incomingUrl) return;

            const target = resolveNativeDeepLinkTarget(incomingUrl);
            if (!target) return;

            if (target.kind === 'inspo_import') {
                WebAnalytics.track(EVENT_DEEP_LINK_OPENED, {
                    deep_link_url: incomingUrl,
                    deep_link_route: 'inspo_import_modal',
                    deep_link_source: source,
                });
                openInspoImport({ prefilledUrl: target.prefilledUrl, entry: 'deep_link' });
                return;
            }

            const destination = target.route;

            if (destination.startsWith('/auth/callback')) {
                void Browser.close().catch(() => {});
            }

            WebAnalytics.track(EVENT_DEEP_LINK_OPENED, {
                deep_link_url: incomingUrl,
                deep_link_route: destination,
                deep_link_source: source,
            });

            router(destination, { replace: true });
        };

        void App.getLaunchUrl()
            .then((launch) => {
                if (launch?.url) {
                    handleIncomingUrl(launch.url, 'launch');
                }
            })
            .catch((launchError) => {
                error('[NativeDeepLinkListener] failed to inspect launch URL', launchError);
            });

        const handle = App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
            try {
                const url = event.url;
                if (!url) return;
                if (!supportsNativeDeepLinkUrl(url)) {
                    return;
                }
                handleIncomingUrl(url, 'event');
            } catch (e) {
                error('[NativeDeepLinkListener] failed to handle appUrlOpen', e);
            }
        });

        return () => {
            void handle.then((h) => h.remove());
        };
    }, [router, openInspoImport]);

    return null;
}
